import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import {
  createElectricitySupplier,
  listElectricitySuppliers,
  updateElectricitySupplier,
} from '../../../api/officesApi';
import { matchesSearch } from '../../../shared/utils/text';
import { normalizeElectricitySupplier } from '../model/scope2Assignment';

const supplierRows = payload => (
  Array.isArray(payload) ? payload : payload?.items || []
);

const sortSuppliers = rows => [...rows].sort((left, right) => (
  left.registrationNo.localeCompare(right.registrationNo)
));

export default function useElectricitySupplierCatalog({ enabled = false } = {}) {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listElectricitySuppliers({ activeOnly: false });
      const normalized = supplierRows(response)
        .map(normalizeElectricitySupplier)
        .filter(supplier => supplier.id && supplier.registrationNo);
      setSuppliers(sortSuppliers(normalized));
      setLoadError(null);
      return normalized;
    } catch (error) {
      setLoadError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) reload().catch(() => { });
  }, [enabled, reload]);

  const saveSupplier = useCallback(async (existing, payload) => {
    setSaving(true);
    try {
      const response = existing
        ? await updateElectricitySupplier(existing.id, payload)
        : await createElectricitySupplier(payload);
      const saved = normalizeElectricitySupplier(response);
      setSuppliers(previous => sortSuppliers(existing
        ? previous.map(supplier => (supplier.id === saved.id ? saved : supplier))
        : [...previous, saved]));
      return saved;
    } catch (error) {
      if (error?.status === 409) await reload().catch(() => { });
      throw error;
    } finally {
      setSaving(false);
    }
  }, [reload]);

  const visibleSuppliers = useMemo(() => suppliers.filter(supplier => matchesSearch(
    search,
    supplier.registrationNo,
    supplier.nameJa,
  )), [search, suppliers]);

  return {
    suppliers: visibleSuppliers,
    total: suppliers.length,
    search,
    setSearch,
    loading,
    loadError,
    saving,
    reload,
    saveSupplier,
  };
}
